import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Printer, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Expenses({ user }) {
  const navigate = useNavigate();
  const { hasPermission, canCreate, canEdit, canDelete, canApprove } = usePermissions(user);
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState(''); // Filter by department/type
  const [fundFilter, setFundFilter] = useState(''); // Filter by fund
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [statusAuthDialogOpen, setStatusAuthDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [selectedExecuteExpenseId, setSelectedExecuteExpenseId] = useState(null);
  const [receiptImages, setReceiptImages] = useState([]);
  const [totalValuePKR, setTotalValuePKR] = useState('');
  const [receiptViewOpen, setReceiptViewOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [formData, setFormData] = useState({
    type: '',
    justification: '',
    fund: '',
    requested_budget: '',
    items: []
  });
  
  // Item input states
  const [itemName, setItemName] = useState('');
  const [itemQuantity, setItemQuantity] = useState('');

  // PKR to USD conversion rate
  const PKR_TO_USD_RATE = 280; // 1 USD = 280 PKR

  const convertPKRtoUSD = (pkr) => {
    if (!pkr || parseFloat(pkr) <= 0) return 0;
    return (parseFloat(pkr) / PKR_TO_USD_RATE).toFixed(2);
  };

  const fundOptions = [
    { value: 'petty_cash', label: 'Petty Cash' },
    { value: 'finance_department', label: 'Finance Department' }
  ];

  const departmentOptions = [
    { value: 'Executive', label: 'Executive' },
    { value: 'Management', label: 'Management' },
    { value: 'Merchandise', label: 'Merchandise' },
    { value: 'Design Studio', label: 'Design Studio' },
    { value: 'Sampling', label: 'Sampling' },
    { value: 'Misc', label: 'Misc' }
  ];

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExpenses(data);
      }
    } catch (error) {
      toast.error('Failed to fetch expenses');
    }
  };

  // Auto-refresh every 5 seconds, but pause when any dialog is open
  const isUserEditing = open || authDialogOpen || viewDialogOpen || statusAuthDialogOpen || executeDialogOpen;
  useAutoRefresh(fetchExpenses, 5, isUserEditing);
  
  const handleAddItem = () => {
    if (!itemName.trim()) {
      toast.error('Item name is required');
      return;
    }
    if (!itemQuantity || parseFloat(itemQuantity) <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }
    
    const newItem = {
      item: itemName.trim(),
      quantity: parseFloat(itemQuantity)
    };
    
    setFormData({ ...formData, items: [...formData.items, newItem] });
    setItemName('');
    setItemQuantity('');
    toast.success('Item added');
  };
  
  const handleRemoveItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
    toast.success('Item removed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.type) {
      toast.error('Department is required');
      return;
    }
    if (!formData.justification.trim()) {
      toast.error('Justification is required');
      return;
    }
    if (!formData.fund) {
      toast.error('Fund is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Prepare data with proper types
      const dataToSubmit = {
        ...formData,
        requested_budget: formData.requested_budget ? parseFloat(formData.requested_budget) : null
      };
      
      console.log('Submitting expense:', dataToSubmit);
      
      if (editingExpense) {
        // Update existing expense
        const response = await fetch(`${API}/expenses/${editingExpense.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(dataToSubmit)
        });

        if (response.ok) {
          toast.success('Expense updated successfully');
          setOpen(false);
          setEditingExpense(null);
          setFormData({ type: '', justification: '', fund: '', requested_budget: '', items: [] });
          fetchExpenses();
        } else {
          const error = await response.json();
          console.error('Update error:', error);
          toast.error(error.detail || 'Failed to update expense');
        }
      } else {
        // Create new expense
        const response = await fetch(`${API}/expenses`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(dataToSubmit)
        });

        if (response.ok) {
          toast.success('Expense created successfully');
          setOpen(false);
          setFormData({ type: '', justification: '', fund: '', requested_budget: '', items: [] });
          fetchExpenses();
        } else {
          const error = await response.json();
          console.error('Create error:', error);
          toast.error(error.detail || 'Failed to create expense');
        }
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(editingExpense ? 'Failed to update expense' : 'Failed to create expense');
    }
  };

  const handleViewClick = (expense) => {
    setSelectedExpense(expense);
    setViewDialogOpen(true);
  };

  const handleEditClick = (expense) => {
    setEditingExpense(expense);
    setFormData({
      type: expense.type || '',
      justification: expense.justification || expense.description || '',
      fund: expense.fund || '',
      requested_budget: expense.requested_budget || '',
      items: expense.items || []
    });
    setOpen(true);
  };

  const handleDeleteClick = (expenseId) => {
    setSelectedExpenseId(expenseId);
    setAuthDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedExpenseId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/expenses/${selectedExpenseId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Expense deleted successfully');
        setAuthDialogOpen(false);
        setSelectedExpenseId(null);
        fetchExpenses();
      } else {
        toast.error('Failed to delete expense');
      }
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleStatusChangeRequest = (expenseId, newStatus) => {
    setPendingStatusChange({ expenseId, newStatus });
    setStatusAuthDialogOpen(true);
  };

  const handleStatusChangeConfirm = async () => {
    if (!pendingStatusChange) return;

    const { expenseId, newStatus } = pendingStatusChange;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/expenses/${expenseId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Status updated to ${newStatus}`);
        setViewDialogOpen(false);
        setStatusAuthDialogOpen(false);
        setPendingStatusChange(null);
        fetchExpenses();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExecuteClick = (expenseId) => {
    setSelectedExecuteExpenseId(expenseId);
    setReceiptImages([]);
    setTotalValuePKR('');
    setExecuteDialogOpen(true);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imagePromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises).then(images => {
      setReceiptImages(prev => [...prev, ...images]);
    });
  };

  const handleRemoveImage = (index) => {
    setReceiptImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleExecuteSubmit = async () => {
    if (receiptImages.length === 0) {
      toast.error('Please upload at least one receipt');
      return;
    }
    if (!totalValuePKR || parseFloat(totalValuePKR) <= 0) {
      toast.error('Please enter a valid total value in PKR');
      return;
    }

    const totalUSD = convertPKRtoUSD(totalValuePKR);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/expenses/${selectedExecuteExpenseId}/execute`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          receipts: receiptImages,
          total_value_pkr: parseFloat(totalValuePKR),
          total_value_usd: parseFloat(totalUSD)
        })
      });

      if (response.ok) {
        toast.success('Expense executed successfully');
        setExecuteDialogOpen(false);
        setSelectedExecuteExpenseId(null);
        setReceiptImages([]);
        setTotalValuePKR('');
        fetchExpenses();
      } else {
        toast.error('Failed to execute expense');
      }
    } catch (error) {
      toast.error('Failed to execute expense');
    }
  };

  const handleReceiptClick = (receipt) => {
    setSelectedReceipt(receipt);
    setReceiptViewOpen(true);
  };

  const handlePrintReceipt = () => {
    if (!selectedReceipt) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Receipt</title>
          <style>
            body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
            img { max-width: 100%; height: auto; }
            @media print {
              body { margin: 0; }
              img { max-width: 100%; page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <img src="${selectedReceipt}" onload="window.print();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrintExpense = (expense) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Expense - ${expense?.expense_code}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { border: 2px solid #000; padding: 15px; margin-bottom: 20px; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 20px 0; }
            .field { margin-bottom: 10px; }
            .label { font-weight: bold; font-size: 12px; color: #666; text-transform: uppercase; }
            .value { font-size: 14px; color: #000; margin-top: 5px; }
            .description-box { border: 1px solid #000; padding: 15px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background: #f0f0f0; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="grid">
              <div>
                <div class="label">Expense Number</div>
                <div class="value" style="font-size: 20px; font-weight: bold;">${expense?.expense_code || ''}</div>
              </div>
              <div>
                <div class="label">Date</div>
                <div class="value">${expense?.date || ''}</div>
              </div>
            </div>
            <div class="grid" style="margin-top: 15px;">
              <div>
                <div class="label">Department</div>
                <div class="value">${expense?.department || expense?.type || 'N/A'}</div>
              </div>
              <div>
                <div class="label">Fund</div>
                <div class="value">${expense?.fund === 'petty_cash' ? 'Petty Cash' : 'Finance Department'}</div>
              </div>
            </div>
            ${expense?.requested_budget ? `
              <div style="border: 1px solid #000; padding: 15px; margin-bottom: 20px; background-color: #f9f9f9;">
                <div class="label">Requested Budget</div>
                <div class="value" style="font-size: 20px; font-weight: bold;">PKR ${parseFloat(expense.requested_budget).toLocaleString()}</div>
              </div>
            ` : ''}
            <div class="grid">
              <div>
                <div class="label">Status</div>
                <div class="value">${expense?.status?.toUpperCase() || ''}</div>
              </div>
              ${expense?.wo_reference ? `
                <div>
                  <div class="label">Work Order Reference</div>
                  <div class="value">${expense.wo_reference}</div>
                </div>
              ` : '<div></div>'}
            </div>
          </div>
          ${expense?.items && expense.items.length > 0 ? `
            <div style="border: 1px solid #000; padding: 15px; margin-bottom: 20px;">
              <div class="label" style="margin-bottom: 10px;">Expense Items</div>
              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="border-bottom: 2px solid #000;">
                    <th style="text-align: left; padding: 8px;">Item</th>
                    <th style="text-align: right; padding: 8px;">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  ${expense.items.map(item => `
                    <tr style="border-bottom: 1px solid #ddd;">
                      <td style="padding: 8px;">${item.item}</td>
                      <td style="text-align: right; padding: 8px;">${item.quantity}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}
          <div class="description-box">
            <div class="label">Justification</div>
            <div class="value">${expense?.justification || expense?.description || ''}</div>
          </div>
          ${expense?.status === 'executed' && (expense?.total_value_pkr > 0 || expense?.total_value_usd > 0) ? `
            <div style="border: 1px solid #000; padding: 15px; margin-bottom: 20px;">
              <div class="label" style="margin-bottom: 10px;">Financial Summary</div>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style="text-align: right;">Amount (PKR)</th>
                    <th style="text-align: right;">Amount (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Expense Total Value</td>
                    <td style="text-align: right;">₨ ${parseFloat(expense?.total_value_pkr || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="text-align: right;">$ ${parseFloat(expense?.total_value_usd || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr style="background: #f0f0f0;">
                    <td style="font-weight: bold;">GRAND TOTAL:</td>
                    <td style="text-align: right; font-weight: bold;">₨ ${parseFloat(expense?.total_value_pkr || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="text-align: right; font-weight: bold;">$ ${parseFloat(expense?.total_value_usd || 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      authorized: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      executed: 'bg-blue-100 text-blue-800'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusOptionsForFund = (fund) => {
    if (fund === 'petty_cash') {
      return [
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' }
      ];
    } else if (fund === 'finance_department') {
      return [
        { value: 'authorized', label: 'Authorized' },
        { value: 'denied', label: 'Denied' }
      ];
    }
    return [
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' }
    ];
  };

  const getAuthCodeForFund = (fund) => {
    if (fund === 'petty_cash') {
      return 'expaprden';
    } else if (fund === 'finance_department') {
      return '1436';
    }
    return 'expaprden';
  };

  const getExpensesByStatus = (status) => {
    let filtered;
    
    if (status === 'approved') {
      filtered = expenses.filter(exp => exp.status === 'approved' || exp.status === 'authorized');
    } else if (status === 'rejected') {
      filtered = expenses.filter(exp => exp.status === 'rejected' || exp.status === 'denied');
    } else {
      filtered = expenses.filter(exp => exp.status === status);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(exp => 
        exp.expense_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exp.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (exp.justification || exp.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedType) {
      filtered = filtered.filter(exp => exp.type === selectedType);
    }
    
    // Apply fund filter
    if (fundFilter) {
      filtered = filtered.filter(exp => exp.fund === fundFilter);
    }
    
    return filtered;
  };
  
  // Get unique funds for filter dropdown
  const getUniqueFunds = () => {
    const funds = [...new Set(expenses.map(exp => exp.fund).filter(Boolean))];
    return funds.map(fund => ({
      value: fund,
      label: fund === 'petty_cash' ? 'Petty Cash' : 'Finance Department'
    }));
  };

  const getUniqueTypes = () => {
    const types = expenses
      .map(exp => exp.type)
      .filter(type => type && type !== 'N/A')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    
    return types.map(type => ({ value: type, label: type }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getDisplayDate = (exp) => {
    if (exp.status === 'approved' || exp.status === 'authorized') {
      return exp.date_of_approval || exp.created_at;
    } else if (exp.status === 'rejected' || exp.status === 'denied') {
      return exp.date_of_rejection || exp.created_at;
    } else if (exp.status === 'executed') {
      return exp.date_of_execution || exp.created_at;
    }
    return exp.created_at;
  };

  const calculateExecutedTotals = () => {
    // Use getExpensesByStatus to apply all filters (search, type, fund)
    const executedExpenses = getExpensesByStatus('executed');
    
    // Separate regular expenses from work order expenses
    const regularExpenses = executedExpenses.filter(exp => !exp.wo_reference);
    const woExpenses = executedExpenses.filter(exp => exp.wo_reference);
    
    const regularTotalPKR = regularExpenses.reduce((sum, exp) => sum + (exp.total_value_pkr || 0), 0);
    const regularTotalUSD = regularExpenses.reduce((sum, exp) => sum + (exp.total_value_usd || 0), 0);
    
    const woTotalPKR = woExpenses.reduce((sum, exp) => sum + (exp.total_value_pkr || 0), 0);
    const woTotalUSD = woExpenses.reduce((sum, exp) => sum + (exp.total_value_usd || 0), 0);
    
    const grandTotalPKR = regularTotalPKR + woTotalPKR;
    const grandTotalUSD = regularTotalUSD + woTotalUSD;
    
    return {
      regularCount: regularExpenses.length,
      regularTotalPKR,
      regularTotalUSD,
      woCount: woExpenses.length,
      woTotalPKR,
      woTotalUSD,
      grandTotalPKR,
      grandTotalUSD,
      totalCount: executedExpenses.length
    };
  };

  const renderExpensesTable = (status) => {
    const expenseList = getExpensesByStatus(status);
    const executedTotals = status === 'executed' ? calculateExecutedTotals() : null;

    return (
      <div className="space-y-4">
        {/* Comprehensive Summary Card for Executed Status */}
        {status === 'executed' && executedTotals && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="grid grid-cols-3 gap-6">
              {/* Regular Expenses */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Regular Expenses</p>
                <div className="space-y-1">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">Count:</span>
                    <span className="text-sm font-semibold text-gray-900">{executedTotals.regularCount}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">PKR:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      ₨ {executedTotals.regularTotalPKR.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">USD:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      $ {executedTotals.regularTotalUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Work Order Expenses */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">External Work Orders</p>
                <div className="space-y-1">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">Count:</span>
                    <span className="text-sm font-semibold text-gray-900">{executedTotals.woCount}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">PKR:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      ₨ {executedTotals.woTotalPKR.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">USD:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      $ {executedTotals.woTotalUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Grand Total */}
              <div className="border-l-2 border-gray-200 pl-6">
                <p className="text-xs font-bold text-gray-700 mb-2 uppercase">Grand Total</p>
                <div className="space-y-1">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">Count:</span>
                    <span className="text-base font-bold text-gray-900">{executedTotals.totalCount}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">PKR:</span>
                    <span className="text-base font-bold text-gray-900">
                      ₨ {executedTotals.grandTotalPKR.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-gray-600">USD:</span>
                    <span className="text-base font-bold text-gray-900">
                      $ {executedTotals.grandTotalUSD.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  EXPENSE CODE
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  DEPARTMENT
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  FUND
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {status === 'pending' ? 'DATE OF CREATION' :
                   status === 'approved' ? 'DATE OF APPROVAL' :
                   status === 'rejected' ? 'DATE OF REJECTION' :
                   status === 'executed' ? 'DATE OF EXECUTION' : 'DATE'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  STATUS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expenseList.map((exp) => (
                <tr key={exp.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewClick(exp)}
                        className="text-sm font-medium text-black hover:underline cursor-pointer"
                      >
                        {exp.expense_code}
                      </button>
                      {exp.wo_reference && (
                        <span className="px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 rounded">
                          {exp.wo_reference}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {exp.type || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {fundOptions.find(f => f.value === exp.fund)?.label || exp.fund || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">{formatDate(getDisplayDate(exp))}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getStatusBadgeClass(exp.status)}`}>
                      {exp.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2 items-center">
                      {exp.status === 'pending' && !exp.wo_reference && (
                        <>
                          {canEdit('expenses') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditClick(exp)}
                              className="border-gray-300 text-gray-600 hover:bg-gray-50"
                            >
                              Edit
                            </Button>
                          )}
                          {canDelete('expenses') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(exp.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                      
                      {exp.status === 'pending' && exp.wo_reference && (
                        <span className="text-xs text-gray-500 italic">Auto-generated from Work Order</span>
                      )}
                      
                      {(exp.status === 'approved' || exp.status === 'authorized') && (
                        <>
                          {hasPermission('expenses_execute') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleExecuteClick(exp.id)}
                              className="border-green-300 text-green-600 hover:bg-green-50"
                            >
                              Execute
                            </Button>
                          )}
                          {hasPermission('expenses_view') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrintExpense(exp)}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                            >
                              Print
                            </Button>
                          )}
                          {canDelete('expenses') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(exp.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}

                      {(exp.status === 'rejected' || exp.status === 'denied' || exp.status === 'executed') && (
                        <>
                          {hasPermission('expenses_view') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrintExpense(exp)}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                            >
                              Print
                            </Button>
                          )}
                          {canDelete('expenses') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteClick(exp.id)}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {expenseList.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No expenses found</p>
            </div>
          )}
        </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Expenses
              </h1>
              <p className="text-sm text-gray-600">Manage expense records</p>
            </div>
          </div>
          {canCreate('expenses') && (
            <Button 
              onClick={() => {
                setEditingExpense(null);
                setFormData({ type: '', justification: '', fund: '', requested_budget: '', items: [] });
                setOpen(true);
              }} 
              className="bg-black hover:bg-gray-800"
            >
              <Plus className="w-4 h-4 mr-2" /> New Expense
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Bar and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Master Search
              </label>
              <Input
                placeholder="Enter Expense Code or Department"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Department
              </label>
              <div className="flex gap-2">
                <SearchableSelect
                  options={getUniqueTypes()}
                  value={selectedType}
                  onChange={(value) => setSelectedType(value)}
                  placeholder="Select department..."
                  searchPlaceholder="Search departments..."
                  emptyText="No departments found"
                  className="flex-1"
                />
                {selectedType && (
                  <Button
                    variant="outline"
                    onClick={() => setSelectedType('')}
                    className="border-gray-300"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Fund
              </label>
              <div className="flex gap-2">
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Funds' },
                    ...getUniqueFunds()
                  ]}
                  value={fundFilter}
                  onChange={(value) => setFundFilter(value)}
                  placeholder="Select fund..."
                  searchPlaceholder="Search funds..."
                  className="flex-1"
                />
                {fundFilter && (
                  <Button
                    variant="outline"
                    onClick={() => setFundFilter('')}
                    className="border-gray-300"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Filters apply across all status tabs and update totals dynamically
          </p>
        </div>

        {/* Status Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: 'pending', label: 'Pending', count: getExpensesByStatus('pending').length },
                { key: 'approved', label: 'Approved/Authorized', count: getExpensesByStatus('approved').length },
                { key: 'rejected', label: 'Rejected/Denied', count: getExpensesByStatus('rejected').length },
                { key: 'executed', label: 'Executed', count: getExpensesByStatus('executed').length }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${activeTab === tab.key
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.label} <span className="ml-2 text-gray-500">{tab.count}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Expenses Table */}
        {renderExpensesTable(activeTab)}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingExpense ? 'Edit Expense' : 'Create Expense'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Department *</Label>
              <SearchableSelect
                options={departmentOptions}
                value={formData.type}
                onChange={(value) => setFormData({ ...formData, type: value })}
                placeholder="Select department..."
                searchPlaceholder="Search department..."
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Fund *</Label>
              <SearchableSelect
                options={fundOptions}
                value={formData.fund}
                onChange={(value) => setFormData({ ...formData, fund: value })}
                placeholder="Select fund..."
                searchPlaceholder="Search fund..."
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Requested Budget (PKR)</Label>
              <Input
                type="number"
                placeholder="PKR"
                value={formData.requested_budget}
                onChange={(e) => setFormData({ ...formData, requested_budget: e.target.value })}
                className="border-2 border-gray-300"
                min="0"
                step="0.01"
              />
            </div>
            
            {/* Items Section */}
            <div className="space-y-2 border-2 border-black p-4 rounded-md">
              <Label className="font-bold">Expense Items</Label>
              
              {/* Add Item Form */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Input
                    placeholder="Item name"
                    value={itemName}
                    onChange={(e) => setItemName(e.target.value)}
                    className="border-2 border-gray-300"
                  />
                </div>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Quantity"
                    value={itemQuantity}
                    onChange={(e) => setItemQuantity(e.target.value)}
                    className="border-2 border-gray-300"
                    min="1"
                  />
                  <Button
                    type="button"
                    onClick={handleAddItem}
                    className="bg-black hover:bg-gray-800 whitespace-nowrap"
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {/* Items List */}
              {formData.items.length > 0 && (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs font-medium">Added Items ({formData.items.length})</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {formData.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded border">
                        <div className="flex-1">
                          <span className="font-medium">{item.item}</span>
                          <span className="text-gray-600 ml-2">× {item.quantity}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Justification *</Label>
              <textarea
                className="w-full px-3 py-2 border rounded-md min-h-[120px] focus:outline-none focus:ring-2 focus:ring-black"
                value={formData.justification}
                onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
                placeholder="Justify the expense..."
                autoComplete="off"
                required
              />
              <p className="text-xs text-gray-500">
                Expense code will be auto-generated (EXP-0001, EXP-0002, etc.)
              </p>
            </div>
            <Button type="submit" className="w-full">
              {editingExpense ? 'Update Expense' : 'Create Expense'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Expense Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Expense Record
            </DialogTitle>
          </DialogHeader>
          {selectedExpense && (
            <div className="space-y-6 py-4">
              {/* Header Section */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Expense Number</Label>
                    <p className="text-xl font-bold mt-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {selectedExpense.expense_code}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</Label>
                    <div className="mt-1">
                      <span className={`px-3 py-1 inline-flex text-sm font-bold rounded-full uppercase ${getStatusBadgeClass(selectedExpense.status)}`}>
                        {selectedExpense.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg border">
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Department</Label>
                  <p className="text-base font-semibold mt-2">{selectedExpense.type || 'N/A'}</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg border">
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Fund Source</Label>
                  <p className="text-base font-semibold mt-2">
                    {fundOptions.find(f => f.value === selectedExpense.fund)?.label || selectedExpense.fund || 'N/A'}
                  </p>
                </div>
              </div>
              
              {/* Requested Budget */}
              {selectedExpense.requested_budget && (
                <div className="bg-white p-4 rounded-lg border">
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Requested Budget</Label>
                  <p className="text-xl font-bold mt-2">PKR {parseFloat(selectedExpense.requested_budget).toLocaleString()}</p>
                </div>
              )}

              {/* Date Tracking Section */}
              <div className="bg-gray-50 p-4 rounded-lg border">
                <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 block">Date Tracking</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Created</Label>
                    <p className="text-sm font-semibold text-gray-900">{formatDate(selectedExpense.created_at)}</p>
                  </div>
                  {selectedExpense.date_of_approval && (
                    <div>
                      <Label className="text-xs text-gray-500">Approved</Label>
                      <p className="text-sm font-semibold text-green-700">{formatDate(selectedExpense.date_of_approval)}</p>
                    </div>
                  )}
                  {selectedExpense.date_of_rejection && (
                    <div>
                      <Label className="text-xs text-gray-500">Rejected</Label>
                      <p className="text-sm font-semibold text-red-700">{formatDate(selectedExpense.date_of_rejection)}</p>
                    </div>
                  )}
                  {selectedExpense.date_of_execution && (
                    <div>
                      <Label className="text-xs text-gray-500">Executed</Label>
                      <p className="text-sm font-semibold text-blue-700">{formatDate(selectedExpense.date_of_execution)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Work Order Reference Section - If applicable */}
              {selectedExpense.wo_reference && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-purple-900">🔗 Linked to External Work Order:</span>
                    <span className="px-3 py-1 text-sm font-bold bg-purple-600 text-white rounded">
                      {selectedExpense.wo_reference}
                    </span>
                  </div>
                  <p className="text-xs text-purple-700 mt-2">
                    This expense was automatically generated from an executed external work order
                  </p>
                </div>
              )}

              {/* Items Section */}
              <div className="bg-white p-4 rounded-lg border">
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 block">Expense Items</Label>
                {selectedExpense.items && selectedExpense.items.length > 0 ? (
                  <div className="space-y-2">
                    {selectedExpense.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded border">
                        <span className="font-medium text-sm">{item.item}</span>
                        <span className="text-sm text-gray-600">Qty: {item.quantity}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No items added</p>
                )}
              </div>

              {/* Justification Section */}
              <div className="bg-white p-4 rounded-lg border">
                <Label className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 block">Justification</Label>
                <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                  {selectedExpense.justification || selectedExpense.description || 'No justification provided'}
                </p>
              </div>

              {/* Status Change Section - Only for Pending and NOT auto-generated */}
              {selectedExpense.status === 'pending' && !selectedExpense.wo_reference && canApprove('expenses') && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <Label className="text-sm font-bold text-gray-900 mb-3 block">Change Expense Status</Label>
                  <SearchableSelect
                    options={getStatusOptionsForFund(selectedExpense.fund)}
                    value=""
                    onChange={(value) => {
                      if (value) {
                        handleStatusChangeRequest(selectedExpense.id, value);
                      }
                    }}
                    placeholder="Select new status..."
                    searchPlaceholder="Search status..."
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    Authorization required to change status
                  </p>
                </div>
              )}
              
              {/* Financial Information - For Executed Expenses */}
              {selectedExpense.status === 'executed' && (
                <div className="bg-green-50 p-5 rounded-lg border border-green-200">
                  <Label className="text-sm font-bold text-gray-900 mb-3 block uppercase tracking-wider">Financial Summary</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-lg border border-green-300">
                      <Label className="text-xs text-gray-500 uppercase tracking-wider">Total (PKR)</Label>
                      <p className="text-2xl font-bold text-green-700 mt-1">
                        {selectedExpense.total_value_pkr ? `₨ ${parseFloat(selectedExpense.total_value_pkr).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '₨ 0.00'}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-green-300">
                      <Label className="text-xs text-gray-500 uppercase tracking-wider">Total (USD)</Label>
                      <p className="text-2xl font-bold text-green-700 mt-1">
                        {selectedExpense.total_value_usd ? `$ ${parseFloat(selectedExpense.total_value_usd).toFixed(2)}` : '$ 0.00'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Receipts Section - For Executed Expenses */}
              {selectedExpense.status === 'executed' && selectedExpense.receipts && selectedExpense.receipts.length > 0 && (
                <div className="bg-white p-5 rounded-lg border">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                      Payment Receipts ({selectedExpense.receipts.length})
                    </Label>
                    <p className="text-xs text-gray-500">Click on any receipt to view and print</p>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {selectedExpense.receipts.map((receipt, idx) => (
                      <div 
                        key={idx} 
                        className="relative group cursor-pointer"
                        onClick={() => handleReceiptClick(receipt)}
                      >
                        <img
                          src={receipt}
                          alt={`Receipt ${idx + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-gray-300 hover:border-blue-500 transition-all duration-200 shadow-sm hover:shadow-md"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                          <span className="text-white opacity-0 group-hover:opacity-100 font-bold text-lg">
                            View
                          </span>
                        </div>
                        <div className="absolute top-2 right-2 bg-black/80 text-white text-xs font-bold px-2 py-1 rounded">
                          #{idx + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setViewDialogOpen(false)}
                >
                  Close
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePrintExpense(selectedExpense)}
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Execute Dialog */}
      <Dialog open={executeDialogOpen} onOpenChange={setExecuteDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Execute Expense
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Upload Receipts *</Label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
              />
              {receiptImages.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {receiptImages.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={img} 
                        alt={`Receipt ${idx + 1}`}
                        className="w-full h-24 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(idx)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Total Value (PKR) *</Label>
              <Input
                type="number"
                step="0.01"
                value={totalValuePKR}
                onChange={(e) => setTotalValuePKR(e.target.value)}
                placeholder="Enter total value in PKR"
                required
              />
              {totalValuePKR && (
                <p className="text-sm text-gray-600">
                  Equivalent: <span className="font-semibold">${convertPKRtoUSD(totalValuePKR)} USD</span>
                  <span className="text-xs text-gray-500 ml-2">(@ 1 USD = {PKR_TO_USD_RATE} PKR)</span>
                </p>
              )}
            </div>

            <Button 
              onClick={handleExecuteSubmit}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Submit & Execute
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Authorization Dialog for Delete */}
      <AuthorizationDialog
        open={authDialogOpen}
        onOpenChange={setAuthDialogOpen}
        onAuthorized={handleDeleteConfirm}
        title="Delete Expense"
        description="Enter authorization code to delete this expense"
        correctCode="1436"
      />

      {/* Authorization Dialog for Status Change */}
      <AuthorizationDialog
        open={statusAuthDialogOpen}
        onOpenChange={setStatusAuthDialogOpen}
        onAuthorized={handleStatusChangeConfirm}
        title="Change Expense Status"
        message={`Enter authorization code to change status to ${pendingStatusChange?.newStatus || ''}`}
        correctCode={selectedExpense ? getAuthCodeForFund(selectedExpense.fund) : 'extwo'}
      />

      {/* Receipt Viewer Dialog */}
      <Dialog open={receiptViewOpen} onOpenChange={setReceiptViewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Receipt Image
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrintReceipt}
                className="flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print Receipt
              </Button>
            </div>
          </DialogHeader>
          {selectedReceipt && (
            <div className="py-4">
              <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
                <img
                  src={selectedReceipt}
                  alt="Receipt"
                  className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
